<template>
    <div class="ui-category-selector">
        <div @click="pick" :class="'ui-select ui-select--type-basic ui-select--icon-position-left is-touched has-label' + (disabled ? ' is-disabled' : '')">
            <div class="ui-select__content">
                <div class="ui-select__label" tabindex="0">
                    <div v-if="label" class="ui-select__label-text">{{ label }}</div>
                    <div class="ui-select__display">
                        <div class="ui-select__display-value" v-html="selectedCategoryName"></div>
                        <span class="ui-icon ui-select__dropdown-button material-icons">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                                <path d="M6.984 9.984h10.03L12 15z"></path>
                            </svg>
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <ui-modal
            ref="modal"
            size="small"
            :title="internalCategoryName"
            class="ui-category-selector-modal"
        >
            <div v-if="isLoading" class="ui-category-loading">
                <ui-progress-circular 
                    :size="64" 
                    :stroke="2" 
                    :indeterminate="true" 
                />
            </div>
            <ui-textbox
                ref="search"
                v-if="shouldShowSearch"
                v-model="searchQuery"
                icon="search"
                placeholder="Search"
                @input="search"
            />
            <ul class="list-group list-group-flush">
                <li 
                    v-if="hasNoResults && !isLoading"
                    class="list-group-item d-flex justify-content-between align-items-center"
                >
                    No categories
                </li>
                <li 
                    v-for="category in sortedCategories" 
                    :key="category[keys.value]"
                    class="list-group-item d-flex justify-content-between align-items-center list-group-item-action"
                    @click="select(category)" 
                >
                    {{ category[keys.label] }}
                    <span 
                        v-if="category[keys.children] && category[keys.children].length > 0"
                        class="material-icons" 
                    >chevron_right</span>
                </li>
            </ul>

            <div 
                v-if="showPreviousLink"
                class="list-group list-group-flush" 
                style="margin-bottom: -.5rem; margin-top: .5rem;"
            >
                <div
                    class="list-group-item d-flex list-group-item-action"
                    style="border-top: none; font-weight: bold;"
                    @click="previous"
                >
                    <span class="material-icons" style="margin-left: -.4rem">chevron_left</span> 
                    Back to {{ previousCategory ? previousCategory[keys.label] : rootCategoryLabel }}
                </div>
            </div>
        </ui-modal>
    </div>
</template>

<style lang="scss">
@import '~keen-ui/src/styles/imports';

.ui-category-selector {
    .ui-category-selector-modal {
        .ui-modal__body { position: relative; min-height: 150px; }
    }

    .ui-category-loading {
        z-index: 1; 
        background: rgba(255,255,255,0.6); 
        position: absolute; 
        top: 0; left: 0; right: 0; height: 100%;

        .ui-progress-circular {
            margin: auto; 
            top: 45%; 
            transform: translateY(-50%);
        }
    }

    .list-group {
        .list-group-item { 
            padding-left: .5rem; 
            padding-right: .5rem; 
            cursor: pointer;
        }
    }

    .ui-select {
        align-items: flex-start;
        display: flex;
        font-family: $font-stack;
        margin-bottom: $ui-input-margin-bottom;
        outline: none;
        position: relative;
        &:hover:not(.is-disabled) {
            .ui-select__label-text {
                color: $ui-input-label-color--hover;
            }
            .ui-select__display {
                border-bottom-color: $ui-input-border-color--hover;
            }
            .ui-select__dropdown-button {
                color: $ui-input-button-color--hover;
            }
        }
        &.is-active:not(.is-disabled) {
            .ui-select__label-text,
            .ui-select__icon-wrapper .ui-icon {
                color: $ui-input-label-color--active;
            }
            .ui-select__display {
                border-bottom-color: $ui-input-border-color--active;
                border-bottom-width: $ui-input-border-width--active;
            }
        }
        &.has-floating-label {
            .ui-select__label-text {
                // Behaves like a block, but width is the width of its content.
                // Needed here so label doesn't overflow parent when scaled.
                display: table;
                &.is-inline {
                    color: $ui-input-label-color; // So the hover styles don't override it
                    cursor: pointer;
                    transform: translateY($ui-input-label-top--inline) scale(1.1);
                }
                &.is-floating {
                    transform: translateY(0) scale(1);
                }
            }
        }
        &.has-label {
            .ui-select__icon-wrapper {
                padding-top: $ui-input-icon-margin-top--with-label;
            }
            .ui-select__dropdown-button {
                top: $ui-input-button-margin-top--with-label;
            }
        }
        &:not(.is-multiple) {
            .ui-select__display {
                height: $ui-input-height;
                line-height: 1;
            }
        }
        &.is-multiple {
            .ui-select__display {
                line-height: 1.4;
                padding-bottom: rem(4px);
                padding-top: rem(4px);
            }
        }
        &.is-invalid:not(.is-disabled) {
            .ui-select__label-text,
            .ui-select__icon-wrapper .ui-icon {
                color: $ui-input-label-color--invalid;
            }
            .ui-select__display {
                border-bottom-color: $ui-input-border-color--invalid;
            }
            .ui-select__feedback {
                color: $ui-input-feedback-color--invalid;
            }
        }
        &.is-disabled {
            .ui-select__display {
                border-bottom-style: $ui-input-border-style--disabled;
                border-bottom-width: $ui-input-border-width--active;
                color: $ui-input-text-color--disabled;
                cursor: default;
            }
            .ui-select__dropdown-button,
            .ui-select__display-value.is-placeholder {
                color: $ui-input-text-color--disabled;
                opacity: $ui-input-button-opacity--disabled;
            }
            .ui-select__icon-wrapper .ui-icon {
                opacity: $ui-input-icon-opacity--disabled;
            }
            .ui-select__feedback {
                opacity: $ui-input-feedback-opacity--disabled;
            }
        }
    }

    .ui-select__label {
        display: block;
        margin: 0;
        outline: none;
        padding: 0;
        width: 100%;
    }

    .ui-select__content {
        flex-grow: 1;
    }

    .ui-select__label-text {
        color: $ui-input-label-color;
        cursor: default;
        font-size: $ui-input-label-font-size;
        line-height: $ui-input-label-line-height;
        margin-bottom: $ui-input-label-margin-bottom;
        transform-origin: left;
        transition: color 0.1s ease, transform 0.2s ease;
    }

    .ui-select__display {
        align-items: center;
        border: none;
        border-bottom-color: $ui-input-border-color;
        border-bottom-style: solid;
        border-bottom-width: $ui-input-border-width;
        color: $ui-input-text-color;
        cursor: pointer;
        display: flex;
        font-family: inherit;
        font-size: $ui-input-text-font-size;
        font-weight: normal;
        min-height: $ui-input-height;
        padding: 0;
        transition: border 0.1s ease;
        user-select: none;
        width: 100%;
    }

    .ui-select__display-value {
        flex-grow: 1;
        &.is-placeholder {
            color: $hint-text-color;
        }
    }

    .ui-select__dropdown-button {
        color: $ui-input-button-color;
        font-size: $ui-input-button-size;
        margin-left: auto;
        margin-right: rem(-4px);
    }
}
</style>

<script>
    export default {
        props: {
            label: {
                type: String,
                default: null
            },
            disabled: {
                type: Boolean,
                default: false
            },
            placeholder: {
                type: String,
                default: 'Pick a category'
            },
            searchEnabled: {
                type: Boolean,
                default: false,
            },
            provider: {
                type: [Array, Function],
                default() {
                    return []
                }
            },
            keys: {
                type: Object,
                default() {
                    return {
                        label: 'label',
                        value: 'value',
                        children: 'children',
                    }
                },
            },
            value: {
                type: [String, Array, Object],
                default: null
            },
            rootCategoryLabel: {
                type: String,
                default: 'Root Category'
            },
            displayNameFormatter: {
                type: Function,
                default: null,
            }
        },
        watch: {
            value: {
                deep: true,
                handler(n,o) {
                    if (n !== o) {
                        this.selectedCategory = n
                    }
                }
            }
        },
        data() {
            return {
                isLoading: false,
                selectedCategory: null,
                internalCategory: null,
                currentCategories: [],
                searchQuery: '',
                history: []
            }
        },
        methods: {
            search: _.debounce(function() {
                this.load().then(categories => {
                    this.currentCategories = categories
                });
            }, 500),
            previous() {
                this.load(this.previousCategory).then((categories) => {
                    this.currentCategories = categories

                    this.internalCategory = this.previousCategory

                    if (this.history.length > 0) {
                        this.history.pop()
                    }
                });
            },
            reset() {
                this.isLoading = false
                this.currentCategories = []
                this.history = []
                this.internalCategory = null
                this.searchQuery = ''
            },
            select(category) {
                this.searchQuery = ''

                this.load(category).then((categories) => {              
                    if (0 === categories.length) {
                        this.selectedCategory = category
                        this.$emit('input', category)
                        this.$refs.modal.close()
                        return
                    }

                    this.currentCategories = categories

                    this._internalCategorySelected(category)
                });
            },
            _internalCategorySelected(category) {
                this.internalCategory = category
                this.history.push(this.internalCategory)
            },
            pick() {
                if (!this.disabled) {
                    this.reset();

                    this.$refs.modal.open()

                    this.load().then(categories => {
                        this.currentCategories = categories

                        this.$nextTick(() => {
                            this.$refs.search.focus()
                        })
                    })
                }
            },
            load(category = null) {
                this.isLoading = true

                // Offset the loading panes height based on the scroll position.
                this.$nextTick(() => {
                    let scrollTop = this.$el.querySelector('.ui-modal__body').scrollTop
                    this.$el.querySelector('.ui-category-loading').style.top = scrollTop + 'px'
                })

                return new Promise((resolve, reject) => {
                    if (typeof this.provider === 'function') {
                        let response = this.provider(category, this.searchQuery);

                        // Support promises.
                        if (typeof response === 'object' && typeof response.then === 'function') {
                           response.then(categories => {
                                //this.currentCategories = categories

                                this.isLoading = false

                                resolve(categories)
                            })
                        }
                    }
                })
            }
        },
        computed: {
            hasNoResults() {
                return 0 === this.sortedCategories.length
            },
            shouldShowSearch() {
                return this.searchEnabled && 
                    !this.internalCategory && 
                    0 === this.history.length &&
                    (
                        0 !== this.currentCategories.length || 
                        this.searchQuery.length > 0
                    )
            },
            internalCategoryName() {
                return this.internalCategory ? 
                    this.internalCategory[this.keys.label] : this.rootCategoryLabel
            },
            selectedCategoryName() {
                if (this.selectedCategory && this.selectedCategory[this.keys.value]) {
                    if (this.displayNameFormatter) {
                        return this.displayNameFormatter(this.selectedCategory)
                    }

                    return this.selectedCategory[this.keys.label]
                }
                
                return this.placeholder
            },
            showPreviousLink() {
                return this.history.length > 0 && this.internalCategory
            },
            previousCategory() {
                return this.history.length > 1 ? 
                    this.history[this.history.length - 2] : null
            },
            sortedCategories() {
                let alphabetical = _.sortBy(this.currentCategories, this.keys.label)
                let probablMiscOptions = []

                alphabetical = alphabetical.filter(item => {
                    let value = item[this.keys.label]

                    let outcome = (
                        -1 === value.indexOf('Other') &&
                        -1 === value.indexOf('Misc')
                    )

                    if (!outcome) {
                        probablMiscOptions.push(item)
                    }

                    return outcome
                })

                return alphabetical.concat(probablMiscOptions)
            }
        }
    }
</script>